/**
 * ViewSwitcher Component
 * 
 * Allows users to toggle between different view modes (grid, list, map)
 * Core reusable component - theme-agnostic
 */

'use client';

import { motion } from 'framer-motion';

export type ViewMode = 'grid' | 'list' | 'map';

export interface ViewSwitcherProps {
  /**
   * Current active view mode
   */
  activeView: ViewMode;
  /**
   * Callback when view changes
   */
  onViewChange: (view: ViewMode) => void;
  /**
   * Labels for each view
   */
  labels?: {
    grid?: string;
    list?: string;
    map?: string;
  };
  /**
   * Custom className
   */
  className?: string;
  /**
   * Show labels alongside icons
   */
  showLabels?: boolean;
}

/**
 * ViewSwitcher Component
 * 
 * Provides toggle buttons for switching between different view modes
 */
export function ViewSwitcher({
  activeView,
  onViewChange,
  labels = {
    grid: 'Grid',
    list: 'List',
    map: 'Map',
  },
  className = '',
  showLabels = false,
}: ViewSwitcherProps) {
  const views: { mode: ViewMode; icon: React.ReactNode; label: string }[] = [
    {
      mode: 'grid',
      label: labels.grid || 'Grid',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      ),
    },
    {
      mode: 'list',
      label: labels.list || 'List',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      ),
    },
    {
      mode: 'map',
      label: labels.map || 'Map',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
      ),
    },
  ];

  return (
    <div className={`inline-flex items-center bg-gray-100 rounded-lg p-1 ${className}`}>
      {views.map((view) => {
        const isActive = activeView === view.mode;
        
        return (
          <button
            key={view.mode}
            onClick={() => onViewChange(view.mode)}
            className={`
              relative flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium
              transition-colors duration-200
              ${isActive 
                ? 'text-gray-900' 
                : 'text-gray-600 hover:text-gray-900'
              }
            `}
            aria-label={view.label}
            aria-pressed={isActive}
          >
            {/* Active background */}
            {isActive && (
              <motion.div
                layoutId="activeViewBg"
                className="absolute inset-0 bg-white rounded-md shadow-sm"
                initial={false}
                transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30,
                }}
              />
            )}
            
            {/* Icon */}
            <span className="relative z-10">{view.icon}</span>
            
            {/* Label (optional) */}
            {showLabels && (
              <span className="relative z-10">{view.label}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

